import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";

import { getDictationAudit, addDictationAudit } from "apiHandler/actions";
import { DataGridCustomPagination } from "components/helpers";
import { useUserData } from "components/customHooks/userDataContext";
import { getFormattedDateTime } from "utils/helper";
import { Close, Refresh } from "@mui/icons-material";

const AuditHistory = (props) => {
  const { audit, setAudit, id, onClose } = props;
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(audit === null);
  const userData = useUserData();
  const token = userData?.access_token;

  useEffect(() => {
    if (audit === null) fetchAudit();
  }, []);

  const fetchAudit = async () => {
    try {
      setAudit([]);
      setLoading(true);
      const payload = {
        type: "Dictation",
        search_criteria: { dictation_id: id },
      };
      const response = await getDictationAudit(payload, token);
      setAudit(response || []);
    } finally {
      setLoading(false);
    }
  };

  const addAudit = async () => {
    if (!comments.trim()) return;
    const payload = {
      type: "Dictation",
      record_data: {
        dictation_id: id,
        scribe_id: userData.email,
        description: comments,
        user_name: userData.first_name + " " + userData.last_name,
      },
    };
    await addDictationAudit(payload, token);
    await fetchAudit();
    setComments("");
  };

  const rows = useMemo(
    () =>
      (audit || []).map((a) => ({
        id: a.id,
        user: a.user,
        user_type: a.user_type,
        event_date: getFormattedDateTime(a.event_date),
        description: a.description,
      })),
    [audit],
  );

  const columns = [
    { field: "user", headerName: "User", flex: 1, minWidth: 120 },
    { field: "user_type", headerName: "Type", flex: 1, minWidth: 120 },
    { field: "event_date", headerName: "Date", flex: 1, minWidth: 150 },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
            py: 1,
          }}
        >
          {params.value}
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "90vh", // set dialog height
          maxHeight: "90vh", // prevent overflow
        },
      }}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <DialogTitle>Audit History</DialogTitle>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <IconButton disabled={loading} onClick={fetchAudit}>
            <Refresh color="secondary" />
          </IconButton>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent dividers>
        <Box sx={{ height: "80%", width: "100%", mb: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            loading={loading}
            pageSizeOptions={[5, 10]}
            getRowHeight={() => "auto"}
            slotProps={{
              loadingOverlay: {
                variant: "linear-progress",
                noRowsVariant: "linear-progress",
              },
            }}
            getRowId={(row) => row.id}
            slots={{
              pagination: DataGridCustomPagination,
              toolbar: GridToolbar,
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 1, fontWeight: 500 }}>Add Comments</Box>
          <textarea
            value={comments}
            className="dcm-textarea"
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            style={{ width: "100%" }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={addAudit} variant="contained" color="primary">
          Save
        </Button>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditHistory;
